/**
 * 車両一覧テーブルを `Ajax` 経由で更新する関数
 * 重複更新を防ぐために `isUpdating` を内部に保持したクロージャとして実装
 *
 * @param {Event} event  イベントオブジェクト
 * @param {Object} [options]  コールバック用オプション
 * @param {Function} [options.onSuccess]  `Ajax` 成功時コールバック
 * @param {Function} [options.onError]  `Ajax` 失敗時コールバック
 * @param {Function} [options.onFinally]  成功・失敗に関わらず最後に呼ばれるコールバック
 */
const updateVehiclesTable = (() => {
    let isUpdating = false;

    return (event, {
        onSuccess = null,
        onError = null,
        onFinally = null,
    } = {}) => {
        // 更新中であれば何もしない
        if (isUpdating) {
            return
        };

        // 車両一覧テーブルの `tbody`
        const $tbody = $('#installable-vehicles-table tbody');

        // モーダルから共通で参照する各種データを取得
        const $modal = $(event.currentTarget).closest('.modal');
        const holdingId = $modal.find('[id$="-holding_id"]').val();
        const companyId = $modal.find('[id$="-company_id"]').val();
        const sectionId = $modal.find('[id$="-section_id"]').val();

        // 必須項目が揃っていない場合は車両一覧テーブルをリセット
        if (!holdingId || !companyId) {
            renderNoVehiclesRow($tbody);
            return;
        }

        const element = event.currentTarget;
        const url = $(element).data('drive-recorder-firmware-update-vehicles-action');
        const data = {
            id: $(element).data('drive-recorder-firmware-id'),
            mode: 'firmware_get_installable_vehicles',
            holding_id: holdingId,
            company_id: companyId,
        };
        const headers = {
            'X-CSRF-TOKEN': getCsrfToken(),
        };

        // 営業所は指定されている場合のみ送信
        if (sectionId && sectionId !== '') {
            data.section_id = sectionId;
        }

        // 更新開始
        isUpdating = true;

        // 車両の一覧を取得
        $.ajax({
            url,
            type: 'get',
            dataType: 'json',
            data,
            headers,
        }).then((response) => {
            // 既存行をクリア
            $tbody.empty();

            // 車両の一覧を取得
            const vehicles = response.vehicles || [];

            if (vehicles.length === 0) {
                // 車両が存在しない場合の行を描画
                renderNoVehiclesRow($tbody);

                // 更新終了
                isUpdating = false;

                return;
            }

            // 車両の行を一覧で描画
            renderVehicles($tbody, vehicles);

            // 成功時コールバック
            if (typeof onSuccess === 'function') {
                onSuccess(response);
            }
        }).catch((jqXHR, textStatus, errorThrown) => {
            // 共通エラーハンドラ
            _error.omitted(jqXHR, textStatus, errorThrown);

            // データ取得エラーの表示行を描画
            renderErrorRow($tbody);

            // 失敗時コールバック
            if (typeof onError === 'function') {
                onError(jqXHR, textStatus, errorThrown);
            }
        }).always(() => {
            // 更新終了
            isUpdating = false;

            // 最終処理コールバック（成功・失敗問わず）
            if (typeof onFinally === 'function') {
                onFinally();
            }
        });
    }
})();

/**
 * 車両デバイス一覧テーブルを `Ajax` 経由で更新する関数
 * 重複更新を防ぐために `isUpdating` を内部に保持したクロージャとして実装
 *
 * @param {Event} event  イベントオブジェクト
 * @param {Object} [options]  コールバック用オプション
 * @param {Function} [options.onSuccess]  `Ajax` 成功時コールバック
 * @param {Function} [options.onError]  `Ajax` 失敗時コールバック
 * @param {Function} [options.onFinally]  成功・失敗に関わらず最後に呼ばれるコールバック
 */
const updateVehicleDevicesTable = (() => {
    let isUpdating = false;

    return (event, {
        onSuccess = null,
        onError = null,
        onFinally = null,
    } = {}) => {
        // 更新中であれば何もしない
        if (isUpdating) {
            return
        };

        // 車両デバイス一覧テーブルの `tbody`
        const $tbody = $('#installable-vehicle-devices-table tbody');

        // モーダルから共通で参照する各種データを取得
        const $modal = $(event.currentTarget).closest('.modal');
        const holdingId = $modal.find('[id$="-holding_id"]').val();
        const companyId = $modal.find('[id$="-company_id"]').val();
        const sectionId = $modal.find('[id$="-section_id"]').val();

        const modelTypeFromSelectForm = $modal.find('[id$="-model_type"]').val();
        const modelTypeFromData = $(event.currentTarget).data('drive-recorder-firmware-model-type');
        const modelType = typeof modelTypeFromData === 'undefined'
            ? modelTypeFromSelectForm
            : modelTypeFromData;

        // 必須項目が揃っていない場合は車両デバイス一覧テーブルをリセット
        if (!holdingId || !companyId || !modelType) {
            renderNoVehicleDevicesRow($tbody);
            return;
        }

        const element = event.currentTarget;
        const url = $(element).data('drive-recorder-firmware-update-vehicle-devices-action');
        const data = {
            id: $(element).data('drive-recorder-firmware-id'),
            mode: 'firmware_get_installable_vehicle_devices',
            holding_id: holdingId,
            company_id: companyId,
            model_type: modelType,
        };
        const headers = {
            'X-CSRF-TOKEN': getCsrfToken(),
        };

        // 営業所は指定されている場合のみ送信
        if (sectionId && sectionId !== '') {
            data.section_id = sectionId;
        }

        // 更新開始
        isUpdating = true;

        // 車両デバイスの一覧を取得
        $.ajax({
            url,
            type: 'get',
            dataType: 'json',
            data,
            headers,
        }).then((response) => {
            // 既存行をクリア
            $tbody.empty();

            // 車両デバイスの一覧を取得
            const vehicleDevices = response.vehicle_devices || [];

            if (vehicleDevices.length === 0) {
                // 車両デバイスが存在しない場合の行を描画
                renderNoVehicleDevicesRow($tbody);

                // 更新終了
                isUpdating = false;

                return;
            }

            // 車両デバイスの行を一覧で描画
            renderVehicleDevices($tbody, vehicleDevices);

            // 成功時コールバック
            if (typeof onSuccess === 'function') {
                onSuccess(response);
            }
        }).catch((jqXHR, textStatus, errorThrown) => {
            // 共通エラーハンドラ
            _error.omitted(jqXHR, textStatus, errorThrown);

            // データ取得エラーの表示行を描画
            renderErrorRow($tbody);

            // 失敗時コールバック
            if (typeof onError === 'function') {
                onError(jqXHR, textStatus, errorThrown);
            }
        }).always(() => {
            // 更新終了
            isUpdating = false;

            // 最終処理コールバック（成功・失敗問わず）
            if (typeof onFinally === 'function') {
                onFinally();
            }
        });
    }
})();

/**
 * 車両が存在しない場合の行を描画
 *
 * @param {*} $tbody
 */
const renderNoVehiclesRow = ($tbody) => {
    $tbody.html(`
        <tr>
            <td colspan="7" class="text-center text-muted">
                該当する車両はありません
            </td>
        </tr>
    `);
};

/**
 * 車両デバイスが存在しない場合の行を描画
 *
 * @param {*} $tbody
 */
const renderNoVehicleDevicesRow = ($tbody) => {
    $tbody.html(`
        <tr>
            <td colspan="7" class="text-center text-muted">
                該当する車両デバイスはありません
            </td>
        </tr>
    `);
};

/**
 * データ取得エラーの表示行を描画
 *
 * @param {*} $tbody
 */
const renderErrorRow = ($tbody) => {
    $tbody.html(`
        <tr>
            <td colspan="7" class="text-center text-danger">
                データの取得に失敗しました
            </td>
        </tr>
    `);
};

/**
 * 車両の行を一覧で描画
 *
 * @param {*} $tbody
 * @param {*} vehicles
 */
const renderVehicles = ($tbody, vehicles) => {
    vehicles.forEach(vehicle => {
        $tbody.append(`
            <tr data-id="${vehicle.id}">
                <td class="text-center">
                    <input type="checkbox"
                        name="vehicles[]"
                        value="${vehicle.id}"
                        data-drive-recorder-checkbox="vehicles"
                        ${vehicle.firmware_release_status ? 'checked disabled' : ''}>
                </td>
                <td class="text-center">${vehicle.holding_name ?? ''}</td>
                <td class="text-center">${vehicle.company_name ?? ''}</td>
                <td class="text-center">${vehicle.section_name ?? ''}</td>
                <td class="text-center">${vehicle.vehicle_code ?? ''}</td>
                <td class="text-center">${vehicle.vehicle_name ?? ''}</td>
                <td class="text-center">${vehicle.firmware_release_status_display ?? ''}</td>
            </tr>
        `);
    });
};

/**
 * 車両デバイスの行を一覧で描画
 *
 * @param {*} $tbody
 * @param {*} vehicleDevices
 */
const renderVehicleDevices = ($tbody, vehicleDevices) => {
    vehicleDevices.forEach(vehicleDevice => {
        $tbody.append(`
            <tr data-id="${vehicleDevice.id}">
                <td class="text-center">
                    <input type="checkbox"
                        name="vehicle_devices[]"
                        value="${vehicleDevice.id}"
                        data-drive-recorder-checkbox="vehicle-devices"
                        ${vehicleDevice.firmware_release_status ? 'checked disabled' : ''}>
                </td>
                <td class="text-center">${vehicleDevice.holding_name ?? ''}</td>
                <td class="text-center">${vehicleDevice.company_name ?? ''}</td>
                <td class="text-center">${vehicleDevice.section_name ?? ''}</td>
                <td class="text-center">${vehicleDevice.vehicle_code ?? ''}</td>
                <td class="text-center">${vehicleDevice.vehicle_name ?? ''}</td>
                <td class="text-center">${vehicleDevice.firmware_release_status_display ?? ''}</td>
            </tr>
        `);
    });
};

/**
 * `meta` タグから `CSRFトークン` を取得
 *
 * @returns {string}  CSRFトークン
 */
const getCsrfToken = () => {
    return $('meta[name="csrf-token"]').attr('content');
};

/**
 * ドラレコファーム関連の関数群をまとめた名前空間オブジェクト
 */
export const DriveRecorderFirmwareHandler = {
    updateVehiclesTable,
    updateVehicleDevicesTable
};
